import React, { useState, useRef, useEffect } from "react";

function TypeaheadInput({
  value,
  onChange,
  suggestions = [],
  placeholder = "Type here...",
  className = "",
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Filter suggestions based on current input
  const filteredSuggestions = suggestions
    .filter(
      (suggestion) =>
        suggestion.toLowerCase().includes(value.toLowerCase()) &&
        suggestion.toLowerCase() !== value.toLowerCase()
    )
    .slice(0, 6); // Limit to 6 suggestions for better UX

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Arrow down
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex < filteredSuggestions.length - 1 ? prevIndex + 1 : 0
      );
    }
    // Arrow up
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredSuggestions.length - 1
      );
    }
    // Enter
    else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      onChange(filteredSuggestions[highlightedIndex]);
      setIsFocused(false);
    }
    // Escape
    else if (e.key === "Escape") {
      setIsFocused(false);
    }
  };

  return (
    <div className="relative w-full z-10">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-md border border-[#7B189F]/30 focus:outline-none focus:ring-2 focus:ring-[#7B189F]/50 shadow-sm text-base ${className}`}
      />

      {isFocused && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-2 bg-white border border-[#7B189F]/30 rounded-lg shadow-xl max-h-60 overflow-auto"
          style={{
            top: "calc(100% + 4px)",
          }}
        >
          <div className="py-1">
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`px-4 py-3 cursor-pointer transition-all duration-150 flex items-center ${
                  index === highlightedIndex
                    ? "bg-[#7B189F]/15 text-[#7B189F] font-medium"
                    : "hover:bg-[#7B189F]/5 text-gray-800"
                }`}
                onClick={() => {
                  onChange(suggestion);
                  setIsFocused(false);
                }}
              >
                <span className="mr-2 text-[#7B189F] opacity-70">
                  {index === highlightedIndex ? "→" : "•"}
                </span>
                {suggestion}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TypeaheadInput;
